import React from 'react'
import { Checkbox, Select, Tooltip } from 'antd'
import type { Organization, OrgRole, OrgSelection } from '../../types'
import { HARD_MONEY_ROLE_OPTIONS } from '../../constants'
import {
  canAddUsersToOrganization,
  hasSuperAdminRole,
  managerCanAssignOwner,
  managerHasSuperAdmin,
  organizationHasPaidSeatLimit,
  organizationPaidUsersLabel,
  organizationSeatUsage,
} from '../../utils'
import OrgAvatar from './OrgAvatar'
import PlanUpgradeHint from './PlanUpgradeHint'
import type { PlanUpgradeIntent } from './PlanUpgradeHint'

interface OrgRolePickerProps {
  organizations: Organization[]
  selections: Record<string, OrgSelection>
  onChange: (orgId: string, patch: Partial<OrgSelection>) => void
  restrictAdds?: boolean
  upgradeIntent?: PlanUpgradeIntent
  currentCustomerId?: string | null
  onUpgrade?: (organization: Organization) => void
  /** Protected roles the person already has in that org; only these stay locked. */
  lockedRolesByOrgId?: Record<string, OrgRole[]>
  /** Organizations that already completed this invite and cannot be changed. */
  disabledOrgIds?: string[]
  /** Allow clearing every role temporarily; callers should validate before submit. */
  allowEmptyRoles?: boolean
  /** Organization ids whose role select should be shown as invalid. */
  invalidRoleOrgIds?: string[]
  /** Saved access for each org; used to hint pending enable/disable changes. */
  accessBaselineByOrgId?: Record<string, boolean>
}

const roleOptionLabel = (role: OrgRole) => {
  if (role === 'Super Admin') return 'Super admin'
  if (role === 'Owner') return 'Account owner'
  if (role === 'Processor') return 'Loan processor'
  return role
}

const OrgRolePicker: React.FC<OrgRolePickerProps> = ({
  organizations,
  selections,
  onChange,
  restrictAdds = false,
  upgradeIntent = 'invite',
  currentCustomerId,
  onUpgrade,
  lockedRolesByOrgId = {},
  disabledOrgIds = [],
  allowEmptyRoles = false,
  invalidRoleOrgIds = [],
  accessBaselineByOrgId,
}) => (
  <div className="um-refactor__org-list">
    {organizations.map((org) => {
      const selection = selections[org.id] || { checked: false, roles: ['Loan Officer'] as OrgRole[] }
      const inviteLocked = disabledOrgIds.includes(org.id)
      const addLocked = restrictAdds && !canAddUsersToOrganization(org) && !selection.checked && !inviteLocked
      const rowLocked = addLocked || inviteLocked
      const seatLabel = organizationPaidUsersLabel(org)
      const seatsFull = organizationHasPaidSeatLimit(org) && !!organizationSeatUsage(org)?.full
      const canAssignSuperAdmin = managerHasSuperAdmin(org)
      const canAssignOwner = managerCanAssignOwner(org)
      const lockedRoles = lockedRolesByOrgId[org.id] || []
      const roleInvalid = invalidRoleOrgIds.includes(org.id) && selection.checked && selection.roles.length === 0
      const cannotUncheck = selection.checked && hasSuperAdminRole(lockedRoles)
      const checkboxDisabled = rowLocked || cannotUncheck
      const standardRoleOptions: OrgRole[] = ['Loan Officer', 'Processor']
      const baseRoleOptions: OrgRole[] = org.isHardMoneyLoanCustomer
        ? [...standardRoleOptions, ...HARD_MONEY_ROLE_OPTIONS]
        : [...standardRoleOptions]
      const availableRoleOptions = canAssignSuperAdmin
        ? [...baseRoleOptions, 'Owner' as OrgRole, 'Super Admin' as OrgRole]
        : canAssignOwner
          ? [...baseRoleOptions, 'Owner' as OrgRole]
        : baseRoleOptions
      const roleOptions = [...availableRoleOptions, ...lockedRoles.filter((role) => !availableRoleOptions.includes(role))]
      const row = (
        <div className={`um-refactor__org-row${rowLocked ? ' um-refactor__org-row--locked' : ''}`}>
          <Tooltip title={cannotUncheck ? 'Super Admin accounts cannot be disabled here' : undefined}>
            <span>
              <Checkbox
                checked={selection.checked}
                disabled={checkboxDisabled}
                onChange={(e) => {
                  if (checkboxDisabled) return
                  onChange(org.id, { checked: e.target.checked })
                }}
              />
            </span>
          </Tooltip>
          <OrgAvatar name={org.name} short={org.short} color={org.color} />
          <div className="um-refactor__org-info">
            <div className="um-refactor__org-name">{org.name}</div>
            <div className="um-refactor__org-address">{org.address}</div>
            {accessBaselineByOrgId && selection.checked !== !!accessBaselineByOrgId[org.id] && (
              <div
                className={`um-refactor__access-delta um-refactor__access-delta--${
                  selection.checked ? 'enable' : 'disable'
                }`}
              >
                {selection.checked ? 'Will enable access' : 'Will disable access'}
              </div>
            )}
            {inviteLocked && (
              <div className="um-refactor__org-complete">Added successfully</div>
            )}
            {seatLabel && (
              <div className={`um-refactor__org-seats${seatsFull ? ' um-refactor__org-seats--full' : ''}`}>
                {seatLabel}
              </div>
            )}
          </div>
          <Select
            className={`um-refactor__role-select${roleInvalid ? ' um-refactor__role-select--invalid' : ''}`}
            popupClassName="um-refactor__role-select-dropdown"
            mode="multiple"
            maxTagCount="responsive"
            placeholder="Select at least one role"
            value={selection.roles}
            disabled={!selection.checked || inviteLocked}
            options={roleOptions.map((role) => ({
              label: roleOptionLabel(role),
              value: role,
              disabled: lockedRoles.includes(role) || (role === 'Super Admin' && !canAssignSuperAdmin),
            }))}
            onChange={(roles: OrgRole[]) => {
              const allowed = roles.filter((role) => {
                if (lockedRoles.includes(role)) return true
                if (role === 'Super Admin') return canAssignSuperAdmin
                if (role === 'Owner') return canAssignOwner
                return true
              })
              const next = [...new Set([...lockedRoles, ...allowed])]
              onChange(org.id, { roles: next.length > 0 || allowEmptyRoles ? next : ['Loan Officer'] })
            }}
          />
        </div>
      )

      if (!addLocked) return <React.Fragment key={org.id}>{row}</React.Fragment>

      return (
        <PlanUpgradeHint
          key={org.id}
          organization={org}
          currentCustomerId={currentCustomerId}
          intent={upgradeIntent}
          onUpgrade={onUpgrade}
        >
          {row}
        </PlanUpgradeHint>
      )
    })}
  </div>
)

export default OrgRolePicker
